/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Dialog lifecycle and initialization effects for registration modal.
 * @module hooks/useAppointmentRegistrationLifecycle
 */

import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { logger, sanitizeError } from "../utils/logger.js";
import { initializeRegistrationModalData } from "../logic/appointments/appointmentRegistrationInit.js";

/**
 * Wires dialog open/close, cancel guard, and requirement-scoped data loading.
 * @param {Object} options
 * @returns {{ initLoading: boolean, initializedRequirementIdRef: React.MutableRefObject<string|null> }}
 */
const useAppointmentRegistrationLifecycle = ({
  dialogRef,
  open,
  onClose,
  isFormLocked,
  selectedRequirement,
  participant,
  resetFormStateForRequirement,
  applyInitializedData,
  setInitLoading,
}) => {
  const navigate = useNavigate();
  const initializedRequirementIdRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog) {
      open ? dialog.showModal() : dialog.close();
    }
  }, [dialogRef, open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    const handleCancel = (event) => {
      if (isFormLocked) {
        event.preventDefault();
      }
    };

    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [dialogRef, isFormLocked]);

  const loadRequirementData = useCallback(
    (requirementId) => {
      setInitLoading(true);
      initializeRegistrationModalData({
        selectedRequirement,
        participant,
        navigate,
        activeRequirementId: requirementId,
      })
        .then((initialized) => {
          if (initializedRequirementIdRef.current === requirementId) {
            applyInitializedData(initialized);
          }
        })
        .catch((error) => {
          logger.error("Error initializing registration modal:", {
            err: sanitizeError(error),
          });
        })
        .finally(() => setInitLoading(false));
    },
    [
      selectedRequirement,
      participant,
      navigate,
      applyInitializedData,
      setInitLoading,
    ],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    const handleClose = () => onClose();
    dialog.addEventListener("close", handleClose);

    if (!open) {
      initializedRequirementIdRef.current = null;
      dialog.close();
      return () => dialog.removeEventListener("close", handleClose);
    }

    const requirementId = selectedRequirement?._id ?? null;
    if (initializedRequirementIdRef.current !== requirementId) {
      initializedRequirementIdRef.current = requirementId;
      resetFormStateForRequirement(
        selectedRequirement?.datosEmpresaUsuario || null,
      );
      loadRequirementData(requirementId);
    }

    dialog.showModal();
    return () => dialog.removeEventListener("close", handleClose);
  }, [
    open,
    onClose,
    selectedRequirement,
    resetFormStateForRequirement,
    loadRequirementData,
    dialogRef,
  ]);

  return { initializedRequirementIdRef };
};

export default useAppointmentRegistrationLifecycle;
